import { getCookieConsent } from '@/lib/cookieHandling'

// Segments sessions in Google Tag Manager / Microsoft Clarity by the account's
// declared market. We deliberately use `Company.country` (a tenant attribute set
// at signup) instead of the UI locale or IP-based geolocation:
// - locale is a per-user preference (a US customer can run the app in German),
//   so it does not reflect the customer's actual market.
// - IP-geo is unreliable (VPN, travel) and would require an extra network request.
//
// This only pushes to the dataLayer; whether/which tag actually fires on
// `market` (e.g. a Microsoft Clarity tag scoped to `market == 'US'`) is
// configured entirely in Google Tag Manager, not here.
let marketPushed = false

export function pushMarketToDataLayer({ country, productType, platform, companySlug } = {}) {
  if (marketPushed || !country) {
    return
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'identity_resolved',
    market: country.toUpperCase(),
    productType,
    platform,
    companySlug,
  })

  marketPushed = true
}

let clarityUserIdentified = false

/**
 * Identifies the current user in Microsoft Clarity, so session recordings can
 * be attributed to a real customer instead of an anonymous visitor.
 * customId is hashed by Clarity client-side before being sent - safe to use a
 * stable internal identifier. friendlyName is NOT hashed and is shown as
 * plain text to anyone with Clarity dashboard access - deliberately includes
 * id/email/name/company here (agreed with the team for this test phase).
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/identify-api
 */
export function identifyClarityUser({ id, email, firstname, lastname, companySlug } = {}) {
  if (
    clarityUserIdentified ||
    typeof window.clarity !== 'function' ||
    !id ||
    !getCookieConsent().measurement
  ) {
    return
  }

  const friendlyName = [[firstname, lastname].filter(Boolean).join(' '), email, companySlug, id]
    .filter(Boolean)
    .join(' · ')

  window.clarity('identify', id, undefined, undefined, friendlyName)
  clarityUserIdentified = true
}
